import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../templates/DefaultLayout';
import { useHistory } from 'react-router';
import { useForm, useAuth } from '~/hooks';
import { getRolesByTitle,getRoleNameByRoleId, LAW_FIRM_USER, ROLES_ID_MAP, TRYON_EMPLOYEE, LAW_FIRM_ADMIN, ROLES, TITLES } from '~/util/auth';
import FormGroup from '~/components/form/form-group';
import FormSelect from '~/components/form/form-select';
import FormRadio from '~/components/form/form-radio';
import FormPassword from '~/components/form/form-password';
import { FaUserPlus } from 'react-icons/fa';
import { getFirms } from '~/api/lawfirm';
import { isEmpty } from 'lodash';
import { auth } from '~/api';
import { auth as authUtil, stringUtils } from '~/util';
import { getOrderContactsByFirm } from '~/api/orderContacts';

const onLoad = async (values) => {
    const firmsData = await getFirms();

    if (Array.isArray(firmsData)) {
        const firmsList = firmsData.map(firm => {
            const { Name, Address1, Address2, City, State, Id } = firm;

            let address = '';

            if (Address1) 
                address = Address1;

            if (Address2) 
                address = `${address} ${Address2}`;

            return {
                id: Id,
                name: Name,
                address,
                city: City,
                state: State
            };
        });

        return {
            ...values,
            firms: firmsList
        };
    }
};

const AddUser = () => {
    const history = useHistory();
    const authCtx = useAuth();
    const onSubmit = async ({ values, onFail, onInvalidForm, resetFormErrors }) => {
        try {
            resetFormErrors();
            let errors = {};

            const isFirmAdmin = authCtx.user.roleId == ROLES.FIRM_ADMIN;

            if (!values.firstName) errors.firstName = 'First name is required';
            if (!values.lastName) errors.lastName = 'Last name is required';
            if (!values.userName) errors.userName = 'Username is required';
            if (values.userName.length < 5) errors.userName = 'Username must have a minimum of 5 characters';
            if (!values.title) errors.title = 'Title is required';
            if (!values.lawFirm && values.title.value != 'Tryon Employee' && !isFirmAdmin) errors.lawFirm = 'Law Firm is required';
            if (!values.emailAddress) errors.emailAddress = 'Email address is required';
            if (!values.password) errors.password = 'Password is required';
            if (values.password !== values.confirmPassword) errors.confirmPassword = 'Password and Confirm password should match';
            if (!isEmpty(errors)) return onInvalidForm(errors);

            let firm = values.lawFirm;

            if (!firm && isFirmAdmin) {
                firm = {
                    label: authCtx.user.firmName,
                    value: authCtx.user.firmId
                };
            }
    
            const payload = {
                firstName: values.firstName,
                emailAddress: values.emailAddress,
                userName: values.userName,
                lastName: values.lastName,
                password: values.password,
                lawFirm: firm.label,
                firmId:  firm.value,
                title: values.title,
                active: values.active,
                roleId: ROLES_ID_MAP[values.role.value]
            };

            if (values.contact?.value != undefined) {
                payload.contactId = values.contact?.value;
            }

            if(values.role.value == LAW_FIRM_USER){
                payload.sendPPTXFile =  values.sendPPTXFile;
            }

            if(!isEmpty(values.address) && !isEmpty(values.city) && !isEmpty(values.stateName)){
                payload.lawfirmAddress = values.address + ', ' + values.city + ', ' + values.stateName;
            }
            const response = await auth.registerUser(payload);
            if(!response || response.errored){
                errors.formError = response.error || 'Failed to add user';
                return onInvalidForm(errors);
            } else {
                history.push('/useradmin');
            }
        } catch (e) {
            return onFail(e.message);
        }
    };
    const {
        state,
        formActions,
        fieldActions
    } = useForm({
        initialState: {
            firstName: '',
            lastName: '',
            lawFirm: null,
            firms: [],
            contact: null,
            contactOptions: [],
            title: '',
            role: '',
            roles: [],
            address: '',
            city: '',
            state: '',
            active: 'true',
            sendPPTXFile: 'true',
            emailAddress: '',
            userName: '',
            password:'',
            confirmPassword:''
        },
        onSubmit,
        onLoad
    });
    
    return (
        <Layout title="Add New User" subtitle="">
            <div className="container flex items-stretch justify-between flex-col py-4">
                <div className="grid grid-cols-4 grid-flow-row gap-x-8 gap-y-4">
                    <FormGroup
                        label="First Name"
                        groupClassName="col-span-2" 
                        labelClassName="text-sm font-medium" 
                        placeholder="First Name"
                        path="firstName"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.firstName}
                        error={state.errors.firstName}
                        {...fieldActions}
                    />
                    <FormGroup
                        label="Last Name"
                        labelClassName="text-sm font-medium"
                        groupClassName="col-span-2"  
                        placeholder="Last Name"
                        path="lastName"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.lastName}
                        error={state.errors.lastName}
                        {...fieldActions}
                    />
                    <FormGroup
                        label="Email Address"
                        labelClassName="text-sm font-medium"   
                        placeholder="Email Address"
                        path="emailAddress"
                        groupClassName="col-span-2"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.emailAddress}
                        error={state.errors.emailAddress}
                        validator={value=>/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value) ? true : state.values.emailAddress && 'Please provide a valid email'}
                        {...fieldActions}
                    />
                    <FormGroup
                        label="Username"
                        labelClassName="text-sm font-medium"  
                        placeholder="Username"
                        path="userName"
                        groupClassName="col-span-2"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.userName}
                        error={state.errors.userName}
                        {...fieldActions}
                    />
                    <FormSelect
                        label="Title"
                        groupClassName="col-span-2 text-sm font-medium" 
                        required
                        options={authCtx.loadAccessibleTitles()}
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        path='title'
                        placeholder="Please select a title"
                        value={state.values.title}
                        error={state.errors.title}
                        {...fieldActions}
                        onChange={(selectedTitle) => {
                            let roles = getRolesByTitle(selectedTitle.value);
                            roles = roles.map(role => {
                                return getRoleNameByRoleId(role);
                            });
                            fieldActions.onChange(roles, 'roles');
                            fieldActions.onChange({
                                label: roles[0],
                                value: roles[0]
                            }, 'role');

                            if (selectedTitle.value == TITLES.TRYON_EMPLOYEE) {
                                fieldActions.onChange(null, 'lawFirm');
                                fieldActions.onChange('', 'address');
                                fieldActions.onChange('', 'city');
                                fieldActions.onChange('', 'stateName');
                                fieldActions.onChange(null, 'contact');
                            }

                            fieldActions.onChange(selectedTitle, 'title');
                        }}
                    />
                    <FormSelect
                        required
                        label="Role"
                        groupClassName="col-span-2 text-sm font-medium" 
                        options={state.values.roles.map(role=>{
                            return{
                                label: role,
                                value: role
                            };
                        })}
                        placeholder="Please select a role"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        path='role'
                        value={state.values.role}
                        error={state.errors.role}
                        {...fieldActions}
                    />
                    {state.values.title.value != TRYON_EMPLOYEE && authCtx.isSiteAdmin &&
                        <>
                            <FormSelect
                                label="Law Firm"
                                isSearchable
                                groupClassName="col-span-2 text-sm font-medium"
                                options={state.values.firms.map(firm => {
                                    return {
                                        label: firm.name,
                                        value: firm.id
                                    };
                                })}
                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                path='lawFirm'
                                value={state.values.lawFirm}
                                error={state.errors.lawFirm}
                                {...fieldActions}
                                onChange={(selectedFirm) => {
                                    const firm = state.values.firms.find(f=>f.id === selectedFirm.value);
                                    fieldActions.onChange(firm.address, 'address');
                                    fieldActions.onChange(selectedFirm, 'lawFirm');
                                    fieldActions.onChange(firm.city, 'city');
                                    fieldActions.onChange(firm.state, 'stateName');

                                    return getOrderContactsByFirm(selectedFirm.value)
                                        .then(c => fieldActions.onChange(c, 'contactOptions'));
                                }}
                            />
                            {stringUtils.hasValue(state.values.lawFirm) && (
                                <FormSelect 
                                    label="SoftPro Contact"
                                    isSearchable
                                    isClearable
                                    groupClassName="col-span-2 text-sm font-medium"
                                    options={state.values.contactOptions.map(o => {
                                        return {
                                            label: `${o.FirstName} ${o.LastName}`,
                                            value: o.Id
                                        };
                                    })}
                                    inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                                    path='contact'
                                    value={state.values.contact}
                                    error={state.errors.contact}
                                    {...fieldActions}
                                />
                            )}
                            <FormGroup
                                label="Address"
                                labelClassName="text-sm font-medium" 
                                readOnly 
                                path="address"
                                groupClassName="col-span-2 col-start-1"
                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                value={state.values.address}
                            />
                            <FormGroup
                                label="City"
                                labelClassName="text-sm font-medium"  
                                path="city"
                                readOnly
                                groupClassName="col-span-1"
                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                value={state.values.city}
                            />
                            <FormGroup
                                label="State"
                                labelClassName="text-sm font-medium" 
                                readOnly
                                path="state"
                                groupClassName="col-span-1"
                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                value={state.values.stateName}
                            />
                        </>
                    }
                    <FormPassword
                        label="Password"
                        groupClassName="col-span-2 text-sm font-medium" 
                        placeholder="Password"
                        path="password"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.password}
                        error={state.errors.password}
                        {...fieldActions}
                        validator={value=> authUtil.isStrongPassword(value) ? true : 'Password must include at least 8 characters, at least one digit, and at least one upper case character'}     
                    />
                    <FormPassword
                        label="Confirm Password"
                        groupClassName="col-span-2 text-sm font-medium"  
                        placeholder="Confirm Password"
                        path="confirmPassword"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.confirmPassword}
                        error={state.errors.confirmPassword}
                        {...fieldActions}
                    />
                </div>
                {(state.values.role.value == LAW_FIRM_USER || state.values.role.value == LAW_FIRM_ADMIN) &&
                    <div className="mt-4">
                        <label className="text-sm font-medium mr-8">Send PXT File?</label>
                        <FormRadio
                            label="Send PPTX File?" 
                            path="sendPPTXFile"
                            groupClassName="col-span-1"
                            inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                            value={state.values.sendPPTXFile}
                            error={state.errors.sendPPTXFile}
                            {...fieldActions}
                            options={[{value: 'true', key:'Yes'},{value: 'false', key: 'No'}]}
                            onChange={(val)=>{
                                fieldActions.onChange(val, 'sendPPTXFile');
                            }}
                        />
                    </div>
                }
                <div className="mt-4">
                    <label className="text-sm font-medium mr-8">Active?</label>
                    <FormRadio
                        name="Active" 
                        path="active"
                        groupClassName="col-span-1"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.active}
                        error={state.errors.active}
                        {...fieldActions}
                        options={[{value: 'true', key:'Yes'},{value: 'false', key: 'No'}]}
                        onChange={(val)=>{
                            fieldActions.onChange(val, 'active');
                        }}
                    />
                </div> 
                {state.errors.formError && (
                    <div className="my-4">
                        <p className="text-red-400 font-medium ml-2">{state.errors.formError}</p>
                    </div>
                )}
                <div className="flex items-center my-3 justify-end">
                    <Link to="/useradmin" className="mr-3 py-2 px-4 rounded-full text-blueGray-100 bg-gray-100 font-semibold">
                            Cancel
                    </Link>
                    <button className="py-2 px-4 text-white text-sm rounded-full flex items-center bg-lightGreen-300" onClick={() => formActions.submit()}>Create Account
                        <FaUserPlus className="ml-2"/>
                    </button>
                </div>
            </div>
        </Layout>
    );
};

export default AddUser;